import React from "react";
import axios from "axios";
import { CircleMarker, Polyline, Popup, Tooltip } from "react-leaflet";
import * as turf from "@turf/turf";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  addColorsToBuses,
  addSiriData,
  busesManipulation,
  checkSubcontractorPKS,
  getAcronym,
  removePKSXXX,
  shapeArrayOperations,
  showColorStatus,
} from "../../utils/helpers";
import { apiKey, apiPrefix, configAgencyName, fetchInterval, zoomRange } from "../../config";
import { setRouteMapRange } from "../../redux/actions";
import MapBusesComponent from "../MapBusesComponent";
import { AgencyContext } from "../../hooks/AgencyContext";

// Todo: no trip progress - fix this!
const AccordionMapLineLayer = ({
  showLineNumbers,
  buses,
  showDeviations,
}: {
  showLineNumbers: boolean;
  buses: { busesOnTime: BusInfo[]; busesTooFast: BusInfo[]; delayedBuses: BusInfo[]; remainingBuses: BusInfo[] };
  showDeviations: boolean;
}): JSX.Element => {
  // console.log({ buses });

  const { t } = useTranslation();
  const dispatch: Dispatch = useAppDispatch();

  const [lineShape, lineStops, currentDirection, selectedLine, showBusStops, routesDetails, agencyName, vehicleSiriData]: [
    Location[],
    BusStopDirection[],
    string,
    string,
    boolean,
    RouteDetails[],
    string,
    VehicleSiriData[]
  ] = useAppSelector((state: RootState) => [
    state?.lineShapeStops?.lineShape,
    state?.lineShapeStops?.lineStops,
    state?.lineShapeStops?.direction,
    state?.lineShapeStops?.selectedLine,
    state?.lineShapeStops?.showBusStops,
    state?.routesInfo?.routesInfo,
    state?.routesInfo?.agencyName,
    state?.activeBlocks?.vehicleSiriData,
  ]);

  //* AgencyContext
  const { agencyId } = React.useContext(AgencyContext) as AgencyContextType;

  //* Stops and Shapes direction 0 and 1
  const [stops_0, setStops_0] = React.useState<Array<BusStop> | null>(null);
  const [stops_1, setStops_1] = React.useState<Array<BusStop> | null>(null);
  const [shape_0, setShape_0] = React.useState<Array<Line> | null>(null);
  const [shape_1, setShape_1] = React.useState<Array<Line> | null>(null);
  const [lineBuses, setLineBuses] = React.useState<Array<BusInfo> | null>(null);
  const [lineBusesToShow, setLineBusesToShow] = React.useState<Array<BusInfo> | null>(null);

  React.useEffect(() => {
    if (lineStops) {
      const stopPointsObject_0 = lineStops.filter((stopSet) => stopSet.id === "0");
      // console.log("stopPointsObject_0", stopPointsObject_0);
      const stopPoints_0 = stopPointsObject_0[0]?.stop as BusStop[];

      const stopPointsObject_1 = lineStops.filter((stopSet) => stopSet.id === "1");
      // console.log("stopPointsObject_1", stopPointsObject_1);
      const stopPoints_1 = stopPointsObject_1[0]?.stop as BusStop[];

      setStops_0(stopPoints_0);
      setStops_1(stopPoints_1);
    }

    if (lineStops && agencyName === "City of Sweetwater") {
      const stopPoints_0 = lineStops[0]?.stop as BusStop[];
      setStops_0(stopPoints_0);
    }
  }, [agencyName, lineStops]);

  React.useEffect(() => {
    if (lineShape) {
      const shapeDirection_0 = lineShape.filter((shape: { directionId: string }) => shape.directionId === "0");
      const shapeDirection_1 = lineShape.filter((shape: { directionId: string }) => shape.directionId === "1");
      // console.log("shapeDirection_0:", shapeDirection_0);
      // console.log("shapeDirection_1:", shapeDirection_1);

      const flatArrayDirection_0 = shapeArrayOperations(shapeDirection_0);
      const flatArrayDirection_1 = shapeArrayOperations(shapeDirection_1);
      // console.log("flatArrayDirection_0:", flatArrayDirection_0);
      setShape_0(flatArrayDirection_0);
      setShape_1(flatArrayDirection_1);
    }
    if (lineShape && agencyName === "City of Sweetwater") {
      // console.log("lineShape:", lineShape);
      const flatArrayDirection_0 = shapeArrayOperations(lineShape);
      setShape_0(flatArrayDirection_0);
    }
  }, [agencyName, lineShape]);

  React.useEffect(() => {
    if (shape_0 && shape_0.length >= 1 && shape_1 && shape_1.length >= 1) {
      // console.log("shape_0:", shape_0, "shape_1:", shape_1);
      const moreFlatArrayDirection_0 = shape_0.flat(1);
      const moreFlatArrayDirection_1 = shape_1.flat(1);
      // console.log("moreFlatArrayDirection_0:", moreFlatArrayDirection_0);

      const moreFlatArrayDirection = ([] as any).concat(moreFlatArrayDirection_0, moreFlatArrayDirection_1);
      // console.log("moreFlatArrayDirection:", moreFlatArrayDirection);

      let multiPointArray = [];
      for (let i = 0; i < moreFlatArrayDirection.length; i++) {
        multiPointArray.push([moreFlatArrayDirection[i].lat, moreFlatArrayDirection[i].lon]);
      }
      // console.log("multiPointArray:", multiPointArray);

      const multiLine = turf.multiPoint(multiPointArray);
      // console.log("multiLine:", multiLine);

      // Map range
      const bbox = turf.bbox(multiLine);
      // console.log({ bbox });

      const routeMapRangeArray = [
        { lat: bbox[0] - zoomRange, lng: bbox[1] - zoomRange },
        { lat: bbox[2] + zoomRange, lng: bbox[3] + zoomRange },
      ];
      // console.log("routeMapRangeArray:", routeMapRangeArray);
      dispatch(setRouteMapRange(routeMapRangeArray));
    }
  }, [dispatch, shape_0, shape_1]);

  //* Fetching buses info for selectedLine
  React.useEffect(() => {
    if (selectedLine && vehicleSiriData) {
      const controller: AbortController = new AbortController();
      const URL: string = `${apiPrefix}/v1/key/${apiKey}/agency/${agencyId}/command/vehiclesDetails?numPreds=3&onlyAssigned=false&r=${selectedLine}`;

      const getBusesForOneLineInfo = async (): Promise<void> => {
        await axios
          .get(URL)
          .then(async ({ data }) => {
            // console.log("data.vehicles", data.vehicles);
            let allVehicles = data?.vehicles;
            // console.log("allVehicles", allVehicles);

            //* Subcontractors PKS checking
            if (configAgencyName === ("PoznanPKS" as string)) {
              allVehicles = checkSubcontractorPKS(allVehicles, "vehicleName");
            }

            //* Remove pksxxx from buses names
            if (configAgencyName === ("PoznanPKS" as string)) {
              allVehicles = removePKSXXX(allVehicles, "vehicleName");
            }

            //* Adding Siri info: monitored: true or false
            allVehicles = await addSiriData(allVehicles, vehicleSiriData);

            await setLineBuses(allVehicles);
          })
          .catch((error) => {
            console.log("error:", error);
          });
      };
      const setFetchInterval = (): void => {
        // console.log("Data was fetched", new Date().toLocaleTimeString());
        getBusesForOneLineInfo();
      };
      setFetchInterval();
      //* Setting up interval for fetching data
      const fetchingInterval = setInterval(setFetchInterval, fetchInterval) as NodeJS.Timer;
      return () => {
        clearInterval(fetchingInterval);
        // console.log("Interval was cleared");
        controller.abort();
      };
    }
  }, [agencyId, selectedLine, vehicleSiriData]);

  React.useEffect(() => {
    if (lineBuses && currentDirection && buses && routesDetails) {
      (async function (): Promise<void> {
        lineBuses?.forEach(async (bus: BusInfo) => (bus.colorStatus = await showColorStatus(bus.schAdh)));
        // console.log({ currentDirection });

        const { busesOnTime, busesTooFast, delayedBuses } = buses || {};
        // console.log("delayedBuses:", delayedBuses);

        const emptyArray = [] as BusInfo[];
        let busesTogether = await emptyArray.concat(busesOnTime, busesTooFast, delayedBuses);
        // console.log("busesTogether:", busesTogether);

        let lineBusesBig = await busesManipulation(lineBuses, busesTogether);
        lineBusesBig = await addColorsToBuses(lineBuses, routesDetails);
        // console.log({ lineBusesBig });

        const vehiclesDirection_0 = await lineBusesBig.filter((vehicle: BusInfo) => vehicle.direction === "0");
        const vehiclesDirection_1 = await lineBusesBig.filter((vehicle: BusInfo) => vehicle.direction === "1");
        // console.log({ vehiclesDirection_0 });
        // console.log({ vehiclesDirection_1 });

        const vehiclesToSet: BusInfo[] =
          currentDirection === "0" ? vehiclesDirection_0 : currentDirection === "1" ? vehiclesDirection_1 : lineBuses;
        // console.log({ vehiclesToSet });

        //* Add Acronym if agency is Miami Beach
        if (configAgencyName === ("MiamiBeach" as string)) {
          for (let i = 0; i < vehiclesToSet.length; i++) {
            vehiclesToSet[i].acronym = await getAcronym(vehiclesToSet[i]?.routeShortName);
          }
        }
        setLineBusesToShow(vehiclesToSet);
      })();
    }
  }, [buses, currentDirection, lineBuses, routesDetails]);

  const direction_0_Options = { color: "Blue", weight: 4 };
  const direction_0_OptionsLight = { color: "DarkMagenta", weight: 2 };
  const direction_1_Options = { color: "Peru", weight: 4 };
  const direction_1_OptionsLight = { color: "Sienna", weight: 2 };

  const PathShapeDirection0 = () =>
    shape_0 &&
    (shape_0.map((elem, index) => {
      return (
        <Polyline
          key={index}
          pathOptions={index === shape_0?.length - 1 ? direction_0_Options : direction_0_OptionsLight}
          positions={elem}
        />
      );
    }) as JSX.Element[]);

  const PathShapeDirection1 = () =>
    shape_1 &&
    (shape_1.map((elem, index) => {
      return (
        <Polyline
          key={index}
          pathOptions={index === shape_1?.length - 1 ? direction_1_Options : direction_1_OptionsLight}
          positions={elem}
        />
      );
    }) as JSX.Element[]);

  const stops_0_Options = { fillColor: "Blue", color: "Blue", fillOpacity: 0.5 };
  const stops_1_Options = { fillColor: "Peru", color: "Peru", fillOpacity: 0.5 };

  const busStops_0 =
    stops_0 &&
    (stops_0.map((elem: BusStop, index: number) => (
      <CircleMarker
        pathOptions={stops_0_Options}
        key={String(elem.lat + " " + elem.lon + " " + index)}
        center={{
          lat: elem.lat,
          lng: elem.lon,
        }}
        radius={8}
      >
        <Tooltip className="span_italic" permanent={true} direction={"right"}>
          {elem.id}
        </Tooltip>
        <Popup>
          <div>
            {t("Nazwa")} <span className="span_bold">{elem.name}</span>
            <br />
            {t("Id")} <span className="span_bold">{elem.id}</span>
          </div>
        </Popup>
      </CircleMarker>
    )) as JSX.Element[]);

  const busStops_1 =
    stops_1 &&
    (stops_1.map((elem: BusStop, index: number) => (
      <CircleMarker
        pathOptions={stops_1_Options}
        key={String(elem.lat + " " + elem.lon + " " + index)}
        center={{
          lat: elem.lat,
          lng: elem.lon,
        }}
        radius={8}
      >
        <Tooltip className="span_italic" permanent={true} direction={"left"}>
          {elem.id}
        </Tooltip>
        <Popup>
          <div>
            {t("Nazwa")} <span className="span_bold">{elem.name}</span>
            <br />
            {t("Id")} <span className="span_bold">{elem.id}</span>
          </div>
        </Popup>
      </CircleMarker>
    )) as JSX.Element[]);

  return (
    <React.Fragment>
      {(currentDirection === "0" || currentDirection === "2") && shape_0 && PathShapeDirection0()}
      {(currentDirection === "1" || currentDirection === "2") && shape_1 && PathShapeDirection1()}
      {showBusStops && (
        <React.Fragment>
          {(currentDirection === "0" || currentDirection === "2") && stops_0 && busStops_0}
          {(currentDirection === "1" || currentDirection === "2") && stops_1 && busStops_1}
        </React.Fragment>
      )}
      {selectedLine && lineBusesToShow && (
        <MapBusesComponent
          showLineNumbers={showLineNumbers}
          busesOnMap={lineBusesToShow}
          showDeviations={showDeviations}
          moreActions={false}
        />
      )}
    </React.Fragment>
  );
};

export default AccordionMapLineLayer;
